import { useCallback } from 'react'
import { find } from 'lodash-es'
import { useModelStore, useResultsStore } from '@editorStores'
import { useElementTypes, useResultsSelection } from '@editorHooks'
import { selectResultsNavigationForSupportTarget } from '../utils'
import useDesignFormNavigationGuard from './useDesignFormNavigationGuard'

export const useSelectResults2Element = (
  currentElementId?: string,
): ((elementGuid: string) => void) => {
  const {
    selectResultsPosition: navigateResultsToSelectedElement,
    selectResultsSurface: selectSurface,
    setResultsSelection,
  } = useResultsSelection()
  const guardDesignFormNav = useDesignFormNavigationGuard()
  const elementTypesByGuid = useElementTypes()
  const verticalSlabs = useModelStore(state => state.model.vertical_slabs)
  const verticalRoofSlabs = useModelStore(state => state.model.vertical_roof_slabs)
  const standardRipChecks = useResultsStore(state => state.standardRipChecks)

  return useCallback(
    (elementGuid: string) => {
      const elementType = elementTypesByGuid[elementGuid]
      const navigation = selectResultsNavigationForSupportTarget({
        guid: elementGuid,
        elementType,
        standardRipChecks,
      })

      // A slab/roof beam target resolves to its surface guid (the selection identifier), so
      // normalize before the unsaved-change guard compares against the current selection.
      const surfaceGuid =
        navigation.kind === 'position' &&
        (navigation.tab === 'slab-beams' || navigation.tab === 'roof-slab-beams')
          ? find(verticalSlabs, s => s.beam?.guid === navigation.guid)?.guid ??
            find(verticalRoofSlabs, s => s.beam?.guid === navigation.guid)?.guid
          : undefined
      const targetGuid = surfaceGuid ?? elementGuid

      if (targetGuid !== currentElementId && guardDesignFormNav()) return

      if (navigation.kind === 'position') {
        // Vertical (roof) slabs no longer have their own pane: open their beam list with the
        // merged Dach/Decke tab, storing the surface guid (never the beam guid).
        if (navigation.tab === 'slabs' || navigation.tab === 'roof-slabs') {
          const hasBeam =
            !!find(verticalSlabs, { guid: navigation.guid })?.beam?.guid ||
            !!find(verticalRoofSlabs, { guid: navigation.guid })?.beam?.guid
          if (hasBeam) {
            selectSurface(
              navigation.guid,
              navigation.tab === 'slabs' ? 'slab-beams' : 'roof-slab-beams',
            )
            return
          }
        }
        // A slab/roof beam target navigates with its normalized surface guid (Bemessung tab).
        if (surfaceGuid) {
          navigateResultsToSelectedElement(surfaceGuid, navigation.tab, navigation.wallSubTab)
          return
        }
        navigateResultsToSelectedElement(navigation.guid, navigation.tab, navigation.wallSubTab)
      } else {
        setResultsSelection([navigation.guid])
      }
    },
    [
      currentElementId,
      guardDesignFormNav,
      elementTypesByGuid,
      navigateResultsToSelectedElement,
      selectSurface,
      setResultsSelection,
      standardRipChecks,
      verticalSlabs,
      verticalRoofSlabs,
    ],
  )
}
